import { useCallback, useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GetGroupTranscriptTags, UpdateGroupTranscriptTags } from '@trinity/chat/api/interfaces/tags';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { GroupSidebarTags } from '@/components/GroupSidebarTags';
import { CardContent } from '@trinity/chat-components/components/presentation/Main/index.js';
import styles from '@/screens/Transcript/style/Tab.Details.module.css';
import type { GroupTagUpdateItem } from '@/types';
import { useWorkspaceTranscriptParams } from './hooks/useWorkspaceTranscriptParams';

type Props = unknown;

export const Details = (props: Props) => {
  return (
    <DetailsContent />
  );
};
Details.displayName = 'Transcript.Tab.Details';

const DetailsContent = (props: Props) => {
  const { transcriptId } = useWorkspaceTranscriptParams();
  const qc = useQueryClient();
  const apiClient = useChatApiClient();

  const tagsQuery = useQuery({
    queryKey: ['get:transcripts/tags', transcriptId],
    queryFn: () => {
      return apiClient.tags.getTranscriptTags({ transcriptId });
    },
  });

  const tagsMutation = useMutation({
    mutationFn: (data: UpdateGroupTranscriptTags.Request) => {
      return apiClient.tags.updateTranscriptTags({
        transcriptId,
        ...data,
      });
    },
    mutationKey: [`put:transcripts/${transcriptId}/tags`],
    onSuccess: data => {
      // invalidateProjectFiles();
      qc.setQueryData(['get:transcripts/tags', transcriptId], (oldData: GetGroupTranscriptTags.Response | undefined) => {
        if (!oldData) return data;
        return { ...oldData, ...data };
      });
      qc.invalidateQueries({ queryKey: ['get:transcripts/tags', transcriptId] });
    },
  });

  const updateTags = useCallback(async (newTags: GroupTagUpdateItem[]) => {
    await tagsMutation.mutate({ transcriptId, tags: newTags });
  }, [transcriptId, tagsMutation]);

  if (tagsQuery.isLoading) return null;

  return (
    <CardContent className={styles.details}>

      <GroupSidebarTags
        tags={tagsQuery.data?.tags || []}
        onSave={updateTags} />

    </CardContent>
  );
};

DetailsContent.displayName = 'Transcript.Tab.Details.Content';